import { createSelector, weakMapMemoize } from 'reselect'
import { ANYWHERE_PLACE_ID } from 'constants/search'

const getStreamingData = (state: App.State, typeaheadId: string) =>
  state.search.typeaheadStreamingData[typeaheadId]

const getPlacesCache = (state: App.State) => state.destination.places

const streamingField = <K extends keyof App.TypeaheadStreamingData>(key: K) =>
  (state: App.State, typeaheadId: string) => getStreamingData(state, typeaheadId)?.[key]

const streamingFieldData = <K extends keyof App.TypeaheadStreamingData>(key: K) =>
  (state: App.State, typeaheadId: string): NonNullable<App.TypeaheadStreamingData[K]> extends { data: infer D } ? D | undefined : undefined => {
    const field = getStreamingData(state, typeaheadId)?.[key]
    if (field && typeof field === 'object' && 'data' in field) {
      return (field as { data: unknown }).data as never
    }
    return undefined as never
  }

export interface TypeaheadDropdownStreamData {
  results?: Array<App.SearchItemV3>
  naturalLanguageSuggestions?: Array<App.SearchItemV3>
  isComplete?: boolean
  streamingError?: boolean
}

export interface TypeaheadDatesAndOccupancy {
  travelDates?: App.TypeaheadExtractedTravelDates
  occupancy?: Array<App.TypeaheadExtractedOccupancy>
}

// Mirrors `getDisplayPlaceId`'s guards (property / ANYWHERE / missing placeIds) but operates
// on the mapped `ExtractedSearchParamsV2` shape stored in Redux, where `placeIds` is flat.
function getEnrichablePlaceId(extracted: App.SearchItemV3['searchItem']['extractedData']): string | undefined {
  if (!extracted || 'property' in extracted) return undefined
  if (!('placeIds' in extracted)) return undefined
  const first = extracted.placeIds?.[0]
  if (!first || first === ANYWHERE_PLACE_ID) return undefined
  return first
}

function enrichTypeaheadResults(
  results: Array<App.SearchItemV3>,
  places: App.State['destination']['places'],
): Array<App.SearchItemV3> {
  return results.map((item) => {
    const placeId = getEnrichablePlaceId(item.searchItem?.extractedData)
    const place = placeId ? places[placeId] : undefined
    if (!place) return item
    const secondaryText = place.location ?? item.format.secondaryText
    return {
      ...item,
      value: place.name,
      format: { ...item.format, mainText: place.name, secondaryText },
      display: { ...item.display, mainText: place.name, secondaryText },
    }
  })
}

export const getEnrichedTypeaheadResults = createSelector(
  [
    (state: App.State, typeaheadId: string) => getStreamingData(state, typeaheadId)?.results?.data,
    (state: App.State) => getPlacesCache(state),
  ],
  (results, places) => {
    if (!results) return undefined
    return enrichTypeaheadResults(results, places)
  },
  { memoize: weakMapMemoize, argsMemoize: weakMapMemoize },
)

export const getTypeaheadDropdownStreamData = createSelector(
  [
    getEnrichedTypeaheadResults,
    streamingFieldData('naturalLanguageSuggestions'),
    streamingField('isComplete'),
    streamingField('streamingError'),
    streamingField('typeaheadId'),
  ],
  (results, naturalLanguageSuggestions, isComplete, streamingError, typeaheadId): TypeaheadDropdownStreamData | undefined => {
    if (!typeaheadId) return undefined
    return {
      results,
      naturalLanguageSuggestions,
      isComplete,
      streamingError,
    }
  },
  { memoize: weakMapMemoize, argsMemoize: weakMapMemoize },
)

const NATURAL_LANGUAGE_ITEM_STREAM_FIELD_SELECTORS = [
  streamingField('typeaheadId'),
  streamingField('isComplete'),
  streamingField('property'),
  streamingField('location'),
  streamingField('travelDates'),
  streamingField('occupancy'),
  streamingField('amenities'),
  streamingField('inclusions'),
  streamingField('inclusionGap'),
  streamingField('shouldSemanticRerank'),
  streamingField('tags'),
  streamingField('chains'),
  streamingField('subChains'),
  streamingField('sortBy'),
  streamingField('price'),
] as const

export const getTypeaheadNaturalLanguageItemStreamData = createSelector(
  [...NATURAL_LANGUAGE_ITEM_STREAM_FIELD_SELECTORS],
  (
    typeaheadId,
    isComplete,
    property,
    location,
    travelDates,
    occupancy,
    amenities,
    inclusions,
    inclusionGap,
    shouldSemanticRerank,
    tags,
    chains,
    subChains,
    sortBy,
    price,
  ): App.TypeaheadStreamingData | undefined => {
    if (!typeaheadId) return undefined

    return {
      typeaheadId,
      isComplete: isComplete ?? false,
      property,
      location,
      travelDates,
      occupancy,
      amenities,
      inclusions,
      inclusionGap,
      shouldSemanticRerank,
      tags,
      chains,
      subChains,
      sortBy,
      price,
    }
  },
  { memoize: weakMapMemoize, argsMemoize: weakMapMemoize },
)

export const getTypeaheadDatesAndOccupancy = createSelector(
  [
    streamingFieldData('travelDates'),
    streamingFieldData('occupancy'),
  ],
  (travelDates, occupancy): TypeaheadDatesAndOccupancy => ({
    travelDates,
    occupancy,
  }),
  { memoize: weakMapMemoize, argsMemoize: weakMapMemoize },
)
